// src/lib/tenant.js — resolve the current school from the subdomain.
//
// <school>.swotify.in  -> "school"   (auto-fills school_code at login)
// swotify.in / www     -> null       (manual school picker fallback)
// localhost            -> ?school=<x> query param, else VITE_DEV_SUBDOMAIN
//
// Everything here is defensive: any failure returns null so the login screen
// falls back to today's manual school picker — never a blank screen.

import { getApiBase } from './apiBase';

const ROOT_DOMAIN = 'swotify.in';
const APEX_HOSTS = new Set(['swotify.in', 'www.swotify.in']);
// Reserved subdomains that are never a school. 'system' is the super-admin
// portal host (system.swotify.in) — see isSystemPortal() — so it must not be
// treated as a tenant subdomain.
const RESERVED = new Set(['www', 'api', 'app', 'admin', 'mail', 'staging', 'dev', 'system']);

// The school-code convention the backend uses for super-admin context.
export const SYSTEM_SCHOOL_CODE = 'SYSTEM';

/**
 * Is the app being served as the super-admin "System Console"?
 * Production: host is system.swotify.in.
 * Local dev: ?school=system (or VITE_DEV_SUBDOMAIN=system), since real
 *   subdomains aren't available on localhost.
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isSystemPortal(hostname) {
  try {
    const host = String(
      hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''),
    )
      .toLowerCase()
      .trim();
    if (!host) return false;
    if (host === `system.${ROOT_DOMAIN}`) return true;
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
      return resolveSubdomain(host) === 'system';
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * @param {string} [hostname]
 * @returns {string|null} the school subdomain, or null for apex/unknown hosts
 */
export function resolveSubdomain(hostname) {
  try {
    const host = String(
      hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''),
    )
      .toLowerCase()
      .trim();
    if (!host) return null;

    // Local dev: ?school=<code> wins, else VITE_DEV_SUBDOMAIN env.
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
      if (typeof window !== 'undefined') {
        const q = new URLSearchParams(window.location.search).get('school');
        if (q && q.trim()) return q.trim().toLowerCase();
      }
      const envSub = (import.meta.env.VITE_DEV_SUBDOMAIN || '').trim().toLowerCase();
      return envSub || null;
    }

    if (APEX_HOSTS.has(host)) return null;
    if (host.endsWith(`.${ROOT_DOMAIN}`)) {
      const sub = host.slice(0, -(`.${ROOT_DOMAIN}`.length));
      // Only first-level subdomains; ignore reserved names.
      if (!sub || sub.includes('.') || RESERVED.has(sub)) return null;
      return sub;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch school metadata for a subdomain. Returns null on any 404/error.
 * @param {string} subdomain
 * @returns {Promise<null | {code:string,name:string,subdomain:string,is_active:boolean,logo_url?:string,theme_color?:string}>}
 */
export async function fetchSchoolBySubdomain(subdomain) {
  if (!subdomain) return null;
  try {
    const base = getApiBase();
    const res = await fetch(
      `${base}/auth/school-by-subdomain/${encodeURIComponent(subdomain)}`,
      { headers: { 'x-swotify-client': 'react' } },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.code ? data : null;
  } catch {
    return null;
  }
}

/**
 * Resolve school by login code (includes schema_name for parent email domains).
 * @param {string} code
 * @returns {Promise<null | {code:string,name:string,subdomain:string,schema_name:string,is_active:boolean}>}
 */
export async function fetchSchoolByCode(code) {
  if (!code) return null;
  try {
    const base = getApiBase();
    const res = await fetch(
      `${base}/auth/school-by-code/${encodeURIComponent(code)}`,
      { headers: { 'x-swotify-client': 'react' } },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.code ? data : null;
  } catch {
    return null;
  }
}

/**
 * Persist tenant identity used for parent login emails: {phone}@{schema}.com
 */
export function persistSchoolTenantMeta(meta) {
  if (!meta || typeof meta !== 'object') return;
  try {
    if (meta.code) localStorage.setItem('swotify_school_code', String(meta.code).trim());
    if (meta.schema_name) {
      localStorage.setItem('swotify_schema_name', String(meta.schema_name).trim());
    }
    if (meta.subdomain) {
      localStorage.setItem(
        'swotify_school_subdomain',
        String(meta.subdomain).trim().toLowerCase(),
      );
    }
  } catch {
    /* ignore */
  }
}

/**
 * Domain label for parent/staff login emails: `{phone}@{domain}.com`.
 *
 * Order MUST match the backend's `tenant_login_domain()`:
 *   subdomain -> schema_name (fallback when a tenant has no subdomain).
 *
 * Subdomain is preferred because it is DNS-safe and is what users already see
 * in the URL, whereas schema names look like `school_demo` — the underscore is
 * invalid in a hostname and used to get these addresses rejected outright.
 * Accounts created before this switch keep their schema-based address; the
 * backend looks under both, so nothing is orphaned.
 *
 * Never falls back to a hardcoded school like "everwin".
 */
export function getParentEmailDomain() {
  const sanitize = (raw) =>
    String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '')
      .replace(/^_+|_+$/g, '');

  if (typeof localStorage !== 'undefined') {
    const sub = sanitize(localStorage.getItem('swotify_school_subdomain'));
    if (sub) return sub;
    const schema = sanitize(localStorage.getItem('swotify_schema_name'));
    if (schema) return schema;
    const code = sanitize(localStorage.getItem('swotify_school_code'));
    if (code) return code;
  }

  const hostSub = sanitize(resolveSubdomain());
  if (hostSub) return hostSub;

  return 'school';
}
